import {
  computed,
  ref
} from "vue";
import {
  defineStore
} from "pinia";

import {
  getScenicCatalog
} from "@/api/gameApi.js";
import {
  setSystemScenicMaps
} from "@/data/systemScenicMaps.js";
import {
  setScenicCollectionProgress
} from "@/game/runtime/systems/scenicCollectionStore.js";
import {
  backendEnabled
} from "@/services/backendMode.js";


export const useScenicCollectionStore =
  defineStore(
    "scenicCollection",
    () => {
      const scenicMaps =
        ref([]);

      const loading =
        ref(false);

      const loadError =
        ref(null);

      const remoteFragments =
        computed(
          () =>
            scenicMaps.value.flatMap(
              scenic =>
                scenic.fragments
                ?? []
            )
        );

      const collectedLevels =
        computed(
          () =>
            remoteFragments.value
              .filter(
                fragment =>
                  fragment.acquired
              )
              .map(
                fragment =>
                  Number(
                    fragment.level
                  )
              )
              .filter(
                level =>
                  Number.isFinite(
                    level
                  )
                  && level > 0
              )
        );

      function syncRuntime() {
        setScenicCollectionProgress({
          collectedLevels:
            collectedLevels.value
        });
      }

      function clear() {
        scenicMaps.value =
          setSystemScenicMaps(
            []
          );

        syncRuntime();
      }

      async function refresh() {
        if (!backendEnabled) {
          clear();
          return null;
        }

        loading.value =
          true;

        loadError.value =
          null;

        try {
          const data =
            await getScenicCatalog();

          scenicMaps.value =
            setSystemScenicMaps(
              Array.isArray(
                data?.maps
              )
                ? data.maps
                : []
            );

          syncRuntime();

          return data;
        }
        catch (error) {
          clear();

          loadError.value =
            error;

          throw error;
        }
        finally {
          loading.value =
            false;
        }
      }

      function fragmentForLevel(
        level
      ) {
        const target =
          Number(level);

        return (
          remoteFragments.value.find(
            fragment =>
              Number(
                fragment.level
              )
              === target
          )
          ?? null
        );
      }

      function hasFragment(
        level
      ) {
        return Boolean(
          fragmentForLevel(
            level
          )?.acquired
        );
      }

      function collectedCount(
        scenic
      ) {
        return (
          scenic?.fragments
          ?? []
        ).filter(
          fragment =>
            fragment.acquired
        ).length;
      }

      function isComplete(
        scenic
      ) {
        const total =
          Math.max(
            0,
            Number(
              scenic?.pieceCount
            )
            || 0
          );

        return (
          total > 0
          && collectedCount(
            scenic
          )
          >= total
        );
      }

      return {
        scenicMaps,
        remoteFragments,
        collectedLevels,
        loading,
        loadError,
        refresh,
        hasFragment,
        collectedCount,
        isComplete
      };
    }
  );
